import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Segmented, Tooltip, Tag, Empty, Skeleton } from "antd";
import {
  AppstoreOutlined,
  TableOutlined,
  ClockCircleOutlined,
  SplitCellsOutlined,
  BranchesOutlined,
  FileZipOutlined,
  DollarOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getExportHistory } from "api/timelines/exportHistoryApi";
import { getDiffsForProject } from "api/diff/diffApi";
import { getProjectTimeline } from "api/commits/commitsApi";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import TimepointModelPane from "components/Coordination/TimepointModelPane";
import GeomDelta, { deltaOf } from "components/Coordination/GeomDelta";
import UnifiedModelTimeline from "components/Coordination/UnifiedModelTimeline";
import ModelVersions from "./ModelVersions";

const T = {
  ink: "#23343E",
  text: "#3A4952",
  muted: "#8D8D8D",
  sub: "#AEAEAE",
  line: "#EFEFEF",
  line2: "#E2E2E2",
  bg: "#FAFAFA",
  wash: "#E9F1F5",
  wash2: "#F3F7F9",
  navy: "#455D6B",
  navyD: "#3A5765",
  navyX: "#23343E",
  orange: "#FE6919",
  added: "#4FC92A",
  addedBg: "#EEFBE9",
  added2: "#317A1B",
  removed: "#FB8080",
  removedBg: "#FFF0F0",
  removed2: "#C53030",
  mod: "#71A0B8",
  modBg: "#E9F1F5",
  mod2: "#3A5765",
  amber: "#E0A43A",
  ambBg: "#FDF4E1",
};

function Pill({ kind = "neutral", children, size = "md" }) {
  const pad = size === "sm" ? "2px 7px" : "3px 9px";
  const fs = size === "sm" ? 9.5 : 10.5;
  const map = {
    neutral: { bg: "#F3F4F5", bd: "#E8EAEC", fg: "#526470" },
    navy: { bg: T.wash, bd: "#D4E3EC", fg: T.navyD },
    added: { bg: T.addedBg, bd: "#CDEEC1", fg: T.added2 },
    removed: { bg: T.removedBg, bd: "#FAD6D6", fg: T.removed2 },
    modified: { bg: T.modBg, bd: "#C8DDE5", fg: T.mod2 },
    orange: { bg: "#FFF2E9", bd: "#FFD9BF", fg: "#B84B0A" },
    amber: { bg: T.ambBg, bd: "#F1DFB1", fg: "#8A6112" },
  };
  const s = map[kind] || map.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: s.bg, border: `1px solid ${s.bd}`, color: s.fg,
      padding: pad, borderRadius: 999,
      fontSize: fs, fontWeight: 600, lineHeight: 1, letterSpacing: ".02em",
    }}>{children}</span>
  );
}

function TypeBadge({ type }) {
  if (type === "model") return <Pill kind="navy" size="sm"><BranchesOutlined /> MODEL</Pill>;
  if (type === "est") return <Pill kind="amber" size="sm"><DollarOutlined /> ESTIMATE</Pill>;
  return <Pill kind="added" size="sm">MODEL + ESTIMATE</Pill>;
}

function NarrativeLine({ narrative, style }) {
  if (!narrative || !narrative.text) return null;
  return (
    <div style={{ fontSize: 12, color: T.navy, fontWeight: 600, lineHeight: 1.45, ...style }}>
      {narrative.text}
      {narrative.approx && (
        <span style={{ color: T.sub, fontWeight: 400, marginLeft: 6, fontSize: 11 }}>
          (approximate until re-exported)
        </span>
      )}
    </div>
  );
}

function AuthorTag({ author }) {
  if (!author?.name) return <span style={{ color: T.sub, fontSize: 11 }}>—</span>;
  const initial = author.name.charAt(0).toUpperCase();
  return (
    <Tooltip title={`${author.name}${author.email ? ` · ${author.email}` : ""}`}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <div style={{
          width: 22, height: 22, borderRadius: "50%", background: T.navy,
          color: "#fff", fontSize: 10, fontWeight: 600,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>{initial}</div>
        <span style={{ fontSize: 11.5, color: T.text, fontWeight: 500 }}>
          {author.name.split(" ")[0]}
        </span>
      </div>
    </Tooltip>
  );
}

// The row's change counts, in the one git-shaped format the whole suite
// uses. It reads BOTH lanes: the estimation diff (`row.diff.summary`)
// and the geometry lane a model push carries on `row.model` - which is
// where a plugin export's numbers live, and why model rows used to show
// no counts at all while estimate rows did.
function DiffBadge({ row, diff, size = "md", inferred = false }) {
  const d = deltaOf(row || { diff });
  if (!d) return null;
  return <GeomDelta {...d} size={size} inferred={inferred} />;
}

function DeltaCell({ delta, deltaPct, currency = "$" }) {
  if (!delta) return <span style={{ color: T.sub, fontSize: 11 }}>—</span>;
  const pos = delta >= 0;
  const fg = pos ? T.added2 : T.removed2;
  const abs = Math.abs(delta);
  const short = abs >= 1000 ? `${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}K` : abs.toLocaleString();
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: fg }}>
      {pos ? <CaretUpOutlined /> : <CaretDownOutlined />}
      {currency}{short}
      {deltaPct != null && deltaPct !== 0 && (
        <span style={{ fontSize: 10.5, fontWeight: 500, color: T.muted }}>
          ({pos ? "+" : ""}{deltaPct}%)
        </span>
      )}
    </span>
  );
}

// ─── Cards layout ─────────────────────────────────────────────
function CardsLayout({ items }) {
  if (items.length === 0) return <Empty description="No exports to show" style={{ padding: 40 }} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map((r, i) => {
        const prev = items[i + 1];
        return (
          <div key={r.id} style={{
            background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8,
            display: "grid", gridTemplateColumns: "90px 1fr 220px",
            overflow: "hidden",
          }}>
            <div style={{
              background: T.wash2, borderRight: `1px solid ${T.line}`,
              padding: "14px 12px",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 2,
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.navyX }}>{moment(r.date).format("D")}</div>
              <div style={{ fontSize: 10.5, fontWeight: 500, color: T.navyD, textTransform: "uppercase", letterSpacing: ".06em" }}>
                {moment(r.date).format("MMM")}
              </div>
              <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{moment(r.date).format("h:mm A")}</div>
            </div>
            <div style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                <TypeBadge type={r.type} />
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>
                  {r.title || (r.revision ? `Revision ${r.revision.revisionNumber || ""}` : "Export")}
                </div>
                {r.milestone && <Pill kind="navy" size="sm">{r.milestone}</Pill>}
                <DiffBadge row={r} />
              </div>
              {r.description && (
                <div style={{ fontSize: 12, color: T.text, marginBottom: 10, lineHeight: 1.5 }}>{r.description}</div>
              )}
              {r.estimate && prev?.estimate && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "8px 10px", background: T.wash2, borderRadius: 6,
                  border: `1px solid ${T.line}`,
                }}>
                  <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 500 }}>
                    vs prev
                  </div>
                  <DeltaCell delta={r.estimate.delta} deltaPct={r.estimate.deltaPct} currency={r.estimate.currency} />
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: T.muted }}>
                    Total: <b style={{ color: T.text }}>{r.estimate.currency}{(r.estimate.total || 0).toLocaleString()}</b>
                  </span>
                </div>
              )}
              <NarrativeLine narrative={r.narrative} style={{ marginTop: 8 }} />
            </div>
            <div style={{
              padding: "14px 16px", borderLeft: `1px solid ${T.line}`, background: T.bg,
              display: "flex", flexDirection: "column", gap: 10,
            }}>
              <AuthorTag author={r.author} />
              <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: T.muted }}>
                {r.model && <span><FileZipOutlined /> {r.model.components || 0} components</span>}
                {r.estimate && <span><DollarOutlined /> {r.estimate.components || 0} line items</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Table layout ────────────────────────────────────────────
function TableLayout({ items }) {
  if (items.length === 0) return <Empty description="No exports to show" style={{ padding: 40 }} />;
  return (
    <div style={{
      background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6,
      overflow: "hidden",
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead style={{ background: T.bg, borderBottom: `1px solid ${T.line}` }}>
          <tr>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Title</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Milestone</th>
            <th style={thStyle}>Author</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Total</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Δ vs prev</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Components</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Diff</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={r.id} style={{ borderBottom: `1px solid ${T.line}`, background: i % 2 ? "#fff" : "#FCFCFC" }}>
              <td style={tdStyle}>
                <div style={{ fontWeight: 500, color: T.text }}>{moment(r.date).format("MMM D, YYYY")}</div>
                <div style={{ fontSize: 10.5, color: T.muted }}>{moment(r.date).format("h:mm A")}</div>
              </td>
              <td style={{ ...tdStyle, fontWeight: 500, color: T.ink }}>
                {r.title || (r.revision ? `Revision ${r.revision.revisionNumber}` : "Export")}
                <NarrativeLine narrative={r.narrative} style={{ fontWeight: 400, marginTop: 3, maxWidth: 420 }} />
              </td>
              <td style={tdStyle}><TypeBadge type={r.type} /></td>
              <td style={tdStyle}>{r.milestone || <span style={{ color: T.sub }}>—</span>}</td>
              <td style={tdStyle}><AuthorTag author={r.author} /></td>
              <td style={{ ...tdStyle, textAlign: "right", fontWeight: 500 }}>
                {r.estimate ? `${r.estimate.currency}${(r.estimate.total || 0).toLocaleString()}` : <span style={{ color: T.sub }}>—</span>}
              </td>
              <td style={{ ...tdStyle, textAlign: "right" }}>
                {r.estimate ? <DeltaCell delta={r.estimate.delta} deltaPct={r.estimate.deltaPct} currency={r.estimate.currency} /> : <span style={{ color: T.sub }}>—</span>}
              </td>
              <td style={{ ...tdStyle, textAlign: "right", color: T.text }}>
                {r.model?.components || r.estimate?.components || 0}
              </td>
              <td style={{ ...tdStyle, textAlign: "right" }}>
                {deltaOf(r) ? <DiffBadge row={r} size="sm" /> : <span style={{ color: T.sub }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thStyle = {
  textAlign: "left", padding: "10px 12px", fontWeight: 600,
  fontSize: 11.5, color: T.text,
};
const tdStyle = { padding: "10px 12px", verticalAlign: "middle" };

// ─── Timeline layout ─────────────────────────────────────────
function TimelineLayout({ items }) {
  if (items.length === 0) return <Empty description="No exports to show" style={{ padding: 40 }} />;
  return (
    <div style={{ position: "relative", paddingLeft: 24 }}>
      <div style={{
        position: "absolute", left: 10, top: 6, bottom: 6,
        width: 2, background: T.line,
      }} />
      {items.map((r, i) => (
        <div key={r.id} style={{ position: "relative", paddingBottom: 16 }}>
          <div style={{
            position: "absolute", left: -20, top: 14,
            width: 14, height: 14, borderRadius: "50%",
            background: r.type === "model" ? T.navy : r.type === "est" ? T.amber : T.added,
            border: "3px solid #fff", boxShadow: `0 0 0 1px ${T.line2}`,
          }} />
          <div style={{
            background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8,
            padding: "12px 16px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
              <TypeBadge type={r.type} />
              <DiffBadge row={r} />
              <span style={{ fontSize: 11.5, color: T.muted, fontWeight: 500 }}>
                {moment(r.date).format("MMM D, YYYY · h:mm A")}
              </span>
              {r.milestone && <Pill kind="navy" size="sm">{r.milestone}</Pill>}
              <span style={{ flex: 1 }} />
              <AuthorTag author={r.author} />
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, marginBottom: 4 }}>
              {r.title || (r.revision ? `Revision ${r.revision.revisionNumber}` : "Export")}
            </div>
            {r.description && (
              <div style={{ fontSize: 12, color: T.text, lineHeight: 1.5, marginBottom: 8 }}>{r.description}</div>
            )}
            <NarrativeLine narrative={r.narrative} style={{ marginBottom: 8 }} />
            <div style={{ display: "flex", gap: 20, fontSize: 11, color: T.muted, marginTop: 6 }}>
              {r.model && <span><FileZipOutlined /> {r.model.components || 0} components</span>}
              {r.estimate && (
                <span>
                  <DollarOutlined /> {r.estimate.currency}{(r.estimate.total || 0).toLocaleString()}
                  {r.estimate.delta ? <> · <DeltaCell delta={r.estimate.delta} deltaPct={r.estimate.deltaPct} currency={r.estimate.currency} /></> : null}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Split layout (list + detail) ────────────────────────────
function SplitLayout({ items, projectId }) {
  const [selectedId, setSelectedId] = useState(items[0]?.id);
  useEffect(() => { if (items[0] && !items.find(i => i.id === selectedId)) setSelectedId(items[0].id); }, [items, selectedId]);
  if (items.length === 0) return <Empty description="No exports to show" style={{ padding: 40 }} />;
  const selected = items.find(i => i.id === selectedId) || items[0];

  return (
    <div style={{
      display: "grid", gridTemplateColumns: "320px 1fr", gap: 16,
      alignItems: "start",
    }}>
      <div style={{
        background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6,
        overflow: "hidden", maxHeight: "calc(100 * var(--vh) - 200px)", overflowY: "auto",
      }}>
        {items.map(r => {
          const isActive = r.id === selected.id;
          return (
            <div
              key={r.id}
              onClick={() => setSelectedId(r.id)}
              style={{
                padding: "10px 12px", cursor: "pointer",
                borderBottom: `1px solid ${T.line}`,
                background: isActive ? T.wash : "#fff",
                borderLeft: isActive ? `3px solid ${T.navy}` : "3px solid transparent",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <TypeBadge type={r.type} />
                <span style={{ fontSize: 10.5, color: T.muted, marginLeft: "auto" }}>
                  {moment(r.date).format("MMM D")}
                </span>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.title || (r.revision ? `Revision ${r.revision.revisionNumber}` : "Export")}
              </div>
              {/* The counts, then the sentence. A list of "MODEL · Aug
                  22 · Project1 · John Tate" rows is four ways of saying
                  the same thing and no way of saying what the push DID -
                  which is the only question that picks one row over
                  another. */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, flexWrap: "wrap" }}>
                {deltaOf(r) ? <DiffBadge row={r} size="sm" inferred={r.scope === "selection"} /> : null}
                <span style={{ fontSize: 10.5, color: T.muted }}>{r.author?.name || "—"}</span>
              </div>
              {r.narrative?.text ? (
                <div
                  title={r.narrative.text}
                  style={{
                    fontSize: 11, color: T.navy, fontWeight: 500, marginTop: 4,
                    lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical", overflow: "hidden",
                  }}
                >
                  {r.narrative.text}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div style={{
        background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8,
        padding: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <TypeBadge type={selected.type} />
          <div style={{ fontSize: 18, fontWeight: 600, color: T.ink }}>
            {selected.title || (selected.revision ? `Revision ${selected.revision.revisionNumber}` : "Export")}
          </div>
          {selected.milestone && <Pill kind="navy" size="sm">{selected.milestone}</Pill>}
          {deltaOf(selected) ? <DiffBadge row={selected} inferred={selected.scope === "selection"} /> : null}
        </div>
        <NarrativeLine narrative={selected.narrative} style={{ marginBottom: 12, fontSize: 13 }} />
        {/* The model at THIS point on the timeline, with this revision's
            diff painted on it. Keyed on the commit, so moving down the
            list loads each one separately instead of stacking viewers. */}
        {/* The viewer takes the height of the pane, not a fixed
            letterbox: this is the working surface of the layout, and the
            metadata under it is a caption. */}
        <div style={{ marginBottom: 14 }}>
          <TimepointModelPane
            projectId={projectId}
            commitId={selected.commitId}
            label={selected.title || (selected.revision ? `revision ${selected.revision.revisionNumber}` : "this export")}
            height="calc(100 * var(--vh) - 430px)"
            minHeight={380}
            autoLoad
          />
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 14 }}>
          {moment(selected.date).format("MMMM D, YYYY · h:mm A")}
          {selected.author ? <> · by {selected.author.name}</> : null}
        </div>
        {selected.description && (
          <div style={{
            padding: "10px 12px", background: T.bg, border: `1px solid ${T.line}`,
            borderRadius: 6, fontSize: 12.5, color: T.text, lineHeight: 1.5, marginBottom: 14,
          }}>{selected.description}</div>
        )}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12,
          marginBottom: 14,
        }}>
          <Stat label="Type" value={selected.type === "both" ? "Model + Estimate" : selected.type === "model" ? "Model" : "Estimate"} />
          <Stat label="Components" value={(selected.model?.components || selected.estimate?.components || 0).toString()} />
          <Stat label="Total" value={selected.estimate ? `${selected.estimate.currency}${(selected.estimate.total || 0).toLocaleString()}` : "—"} />
          <Stat label="Δ vs prev" value={selected.estimate?.delta ? <DeltaCell delta={selected.estimate.delta} deltaPct={selected.estimate.deltaPct} currency={selected.estimate.currency} /> : "—"} />
          <Stat label="Geometry" value={deltaOf(selected)
            ? <DiffBadge row={selected} inferred={selected.scope === "selection"} />
            : "—"} />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 4 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{value}</div>
    </div>
  );
}

// Commit rows (GET /commits/timeline) → the shape this page renders.
//
// A push is a model export, so it maps to type "model" — or "both" when
// the estimation lane has been joined onto the same commit, which is the
// whole point of Diff.commitId. Geometry counts come from CommitDiff.
//
// Ids are prefixed "commit:" so they can never collide with a timeline
// id in selection state; both live in one list now.
function mapCommitRows(payload) {
  const rows = (payload && payload.rows) || [];
  return rows.map((r) => {
    const g = r.geometry || null;
    const est = r.estimation || null;
    const hasEst = !!(est && est.summary);
    const authorName = r.user
      ? [r.user.name, r.user.lastName].filter(Boolean).join(" ") || r.user.email
      : null;

    // Changed tiles are reported per category; the row wants one number.
    let tileCount = 0;
    if (g && g.changedTilesByCategory) {
      const seen = new Set();
      Object.values(g.changedTilesByCategory).forEach((list) =>
        (list || []).forEach((t) => seen.add(t))
      );
      tileCount = seen.size;
    }

    return {
      id: `commit:${r.commitId}`,
      commitId: r.commitId,
      kind: "commit",
      date: r.ts,
      title: r.label || r.revitDocTitle || "Model push",
      description: "",
      milestone: "",
      author: r.user ? { _id: r.user._id, name: authorName, email: r.user.email } : null,
      revision: null,
      // Issue linkage is what separates "general edit" from "resolving
      // MK-12" on the timeline.
      forIssueIds: r.forIssueIds || [],
      // A selection push cannot testify to deletions; the delta badge
      // says so in the tooltip on its "−" cell.
      scope: r.scope || "full",
      reviewStatus: r.reviewStatus || "approved",
      type: hasEst ? "both" : "model",
      model: {
        size: null,
        components: g ? (g.added || 0) + (g.modified || 0) + (g.unchanged || 0) : null,
        exportedBy: r.user ? r.user._id : null,
        added: g ? g.added : null,
        modified: g ? g.modified : null,
        removed: g ? g.removed : null,
        changedTiles: tileCount,
      },
      estimate: hasEst
        ? {
            total: null,
            components: est.summary.componentsAdded != null
              ? (est.summary.componentsAdded || 0)
                + (est.summary.componentsModified || 0)
                + (est.summary.componentsUnchanged || 0)
              : null,
            delta: 0,
            deltaPct: 0,
            currency: "$",
            jsonPath: null,
            excelPath: null,
            status: est.status,
            diffId: est.diffId,
          }
        : null,
      summary: g
        ? `+${g.added || 0} ~${g.modified || 0} -${g.removed || 0}`
        : "",
      // What the push actually did, in words - the same sentence the
      // revision analytics and the model-edits drawer show, built once
      // when the diff was computed (back/utils/editNarrative.js).
      narrative: (g && g.narrative) || null,
    };
  });
}

// ─── Main page ────────────────────────────────────────────────
export default function ExportHistoryPage() {
  const { projectId } = useParams();
  const [items, setItems] = useState([]);
  const [diffMap, setDiffMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [dataType, setDataType] = useState("both");
  const [layout, setLayout] = useState("timeline");
  // Named in the breadcrumb and the heading: "Export History" alone does
  // not say WHOSE exports these are.
  const [projectTitle, setProjectTitle] = useState("");

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    Promise.all([
      getExportHistory(projectId),
      getDiffsForProject(projectId).catch(() => ({ data: { timelineDiffs: {}, exportDiffs: [] } })),
      // Model pushes from the plugin. They are NOT in export-history:
      // that list is built from Timelines, and a plugin export goes
      // through /projects/export, which never creates one. So these rows
      // are additive rather than duplicates of the estimate rows.
      getProjectTimeline(projectId).catch(() => ({ rows: [] })),
    ])
      .then(([hist, diffs, timeline]) => {
        if (cancelled) return;
        const timelineItems = hist.history || [];
        // The project's name, from whichever payload carries it.
        const named = (timeline?.rows || []).find((r) => r.project && r.project.title)
          || (timelineItems.find((t) => t.projectTitle) || null);
        setProjectTitle(
          hist.projectTitle || named?.project?.title || named?.projectTitle || ""
        );
        const pushes = mapCommitRows(timeline);
        // One list, newest first, so an estimate and the push it came
        // from read in context instead of in two disconnected places.
        const merged = [...timelineItems, ...pushes].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setItems(merged);
        setDiffMap((diffs && diffs.data && diffs.data.timelineDiffs) || {});
      })
      .catch(e => { if (!cancelled) setErr(e.message || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  const enriched = useMemo(
    () => items.map(it => ({ ...it, diff: diffMap[it.id] || null })),
    [items, diffMap]
  );

  const filtered = useMemo(() => {
    if (dataType === "both") return enriched;
    if (dataType === "model") return enriched.filter(i => i.type === "model" || i.type === "both");
    return enriched.filter(i => i.type === "est" || i.type === "both");
  }, [enriched, dataType]);

  const counts = useMemo(() => ({
    all: items.length,
    model: items.filter(i => i.type === "model" || i.type === "both").length,
    est: items.filter(i => i.type === "est" || i.type === "both").length,
  }), [items]);

  return (
    <div style={{ padding: "24px 32px", background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
      {/* This page is reached from a project and from the revision
          analytics, and had no way back to either - the browser's back
          button was the whole navigation story. */}
      {/* The trail follows how an update is actually investigated:
          Coordination -> which project -> what changed over time
          (Revisions) -> the exports behind it (here). The full latest
          model is NOT a step in that chain - it is where you end up, so
          it is an action on the right rather than a crumb wedged
          between the diffs. */}
      <CoordinationBreadcrumbs
        items={[
          ...(projectId ? [{ label: projectTitle || "Project", to: `/coordination/${projectId}` }] : []),
          { label: "Revisions", to: "/analytics/models" },
        ]}
        current="Export history"
      />
      <div style={{ marginBottom: 20, marginTop: 8 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.ink, letterSpacing: "-.01em" }}>
          Export History
          {projectTitle ? <span style={{ fontSize: 15, fontWeight: 500, color: T.muted }}> · {projectTitle}</span> : null}
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
          {counts.all} export{counts.all === 1 ? "" : "s"} · {counts.model} model · {counts.est} estimate
        </div>
      </div>

      <ModelVersions projectId={projectId} />

      <div style={{
        display: "flex", gap: 12, alignItems: "center",
        marginBottom: 20, flexWrap: "wrap",
      }}>
        {layout !== "timeline" && (
          <Segmented
          value={dataType}
          onChange={setDataType}
          options={[
            { value: "both", label: `All (${counts.all})` },
            { value: "model", label: `Model (${counts.model})`, icon: <BranchesOutlined /> },
            { value: "est", label: `Estimate (${counts.est})`, icon: <DollarOutlined /> },
          ]}
        />
        )}
        <span style={{ flex: 1 }} />
        <Segmented
          value={layout}
          onChange={setLayout}
          options={[
            // Ordered the way an update gets investigated: when did it
            // happen, what was it, compare precisely, then look at it
            // against the model.
            { value: "timeline", icon: <ClockCircleOutlined />, label: "Timeline" },
            { value: "cards", icon: <AppstoreOutlined />, label: "Cards" },
            { value: "table", icon: <TableOutlined />, label: "Table" },
            { value: "split", icon: <SplitCellsOutlined />, label: "Model" },
          ]}
        />
      </div>

      {err && (
        <div style={{
          padding: 16, background: T.removedBg, border: `1px solid #FAD6D6`,
          borderRadius: 6, color: T.removed2, fontSize: 13, marginBottom: 16,
        }}>{err}</div>
      )}

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : layout === "cards" ? (
        <CardsLayout items={filtered} />
      ) : layout === "table" ? (
        <TableLayout items={filtered} />
      ) : layout === "timeline" ? (
        <UnifiedModelTimeline projectId={projectId} />
      ) : (
        <SplitLayout items={filtered} projectId={projectId} />
      )}
    </div>
  );
}
